import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { HotToastService } from '@ngneat/hot-toast';

import { AddShoppingCartItemsForChildAccountsRequest, ChildUserAccountDto, ProductDto } from 'src/app/core/dtos/dto';
import { AccountService } from 'src/app/core/services/account.service';
import { ShoppingCartService } from 'src/app/core/services/shopping-cart.service';
import { HttpErrorResponse } from '@angular/common/http';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { BrandEnum, ProductTypeEnum } from 'src/app/core/dtos/enum';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';
import { ProductService } from 'src/app/core/services/product.service';

@Component({
  selector: 'app-product-modal',
  templateUrl: './product-modal.component.html',
  styleUrls: ['./product-modal.component.scss']
})
export class ProductModalComponent implements OnInit, OnDestroy {
  @Input() product: ProductDto;
  @Input() brandId: BrandEnum;
  @Input() isBusinessAccount: boolean;
  @Input() isRegFlow: boolean;
  @Output() addItem: EventEmitter<ProductDto> = new EventEmitter();
  @Output() download: EventEmitter<ProductDto> = new EventEmitter();

  accountSubscription: Subscription;
  batchAddSubscription: Subscription;
  downloadPdfSubscription: Subscription
  childAccounts: ChildUserAccountDto[];
  canBuyForChild: boolean;
  freeForDownload: boolean;
  hasNgCollapse: boolean;
  collapsibleDescription: string;
  nonCollapsibleDescription: string;
  isCollapsed = true;
  electronicProductId = ProductTypeEnum.ElectronicProductFile;
  productDescription: string;

  constructor(
    public activeModal: NgbActiveModal,
    private accountService: AccountService,
    private shoppingCartService: ShoppingCartService,
    private router: Router,
    private toastService: HotToastService,
    private productService: ProductService,
    private configHelper: ConfigHelper
  ) {
  }

  public get BrandEnum() {
    return BrandEnum;
  }

  ngOnInit(): void {
    //use ng-bootstrap instead of the current regular bootstrap
    this.addNgbCollapseToDescription(this.product.description);
    this.freeForDownload = this.product.productTypeId === ProductTypeEnum.ElectronicProductEBook && this.product.isPurchasable && this.product.listPrice === 0;
    if (this.isBusinessAccount) {
      //check for child accounts
      this.accountSubscription = this.accountService.getBusinessAccount()
        .subscribe(x => {
          this.childAccounts = x.content.childUserAccounts;
          if (this.childAccounts.length > 0 && !this.freeForDownload) {
            this.canBuyForChild = true;
          }
          else {
            this.canBuyForChild = false;
          }
        });
    }
    this.productDescription = this.removeInlineStyleProductDescription(this.product.description);

  }

  addNgbCollapseToDescription(description: string) {
    const pattern = /data-toggle="collapse"/;
    if (description.match(pattern)) {
      this.hasNgCollapse = true;
      const splitArray = description.split('Read more');
      const beforeReadMore = splitArray[0];
      const afterReadMore = splitArray[1];

      //The technical requirement for practice test sets should not be collapsed
      if (this.product.productTypeId === ProductTypeEnum.ElectronicProductOnline) {
        const technicalRequirementsIndex = afterReadMore.indexOf('<h3><strong');
        const technicalRequirements = afterReadMore.slice(technicalRequirementsIndex);
        this.nonCollapsibleDescription = beforeReadMore + technicalRequirements;
        this.collapsibleDescription = afterReadMore.match(/<h3[^"]+?\<\/ul>/)[0];
      }
      else {
        this.nonCollapsibleDescription = beforeReadMore;
        const separatorIndex = afterReadMore.indexOf('<h3>');
        this.collapsibleDescription = afterReadMore.slice(separatorIndex);
      }
    }
  }

  onAddItemToCart(product: ProductDto) {
    this.addItem.emit(product);
    this.activeModal.close();
  }

  onContinue() {
    this.activeModal.close();
  }

  onBatchAddItemToCart(selectedChildAccountsIds: number[]) {
    const addShoppingCartItemsForChildAccountsRequest: AddShoppingCartItemsForChildAccountsRequest = {
      childAccountIds: selectedChildAccountsIds,
      discountCodeId: this.product.discountCodeId,
      productId: this.product.id
    };
    this.batchAddSubscription = this.shoppingCartService.batchAddItem(addShoppingCartItemsForChildAccountsRequest)
      .subscribe(
        result => {
          this.shoppingCartService.setShoppingCart(result);
          this.activeModal.close();
          if (this.isRegFlow) {
            this.router.navigate(['/registration/order-summary']);
          }
          else {
            this.router.navigate(['/shopping-cart']);
          }
        },
        (error: HttpErrorResponse) => {
          if (error.status === 400) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            errorMessage.reasons.forEach(reasons => {
              this.toastService.warning(reasons.errorMessage);
            });
          }
          else {
            this.toastService.warning('Unable to add item to cart');
          }
          this.activeModal.close();
        }
      );
  }

  downloadPdf(product: ProductDto) {
    this.downloadPdfSubscription = this.productService.downloadProductPdf(product.name)
      .subscribe(
        res => {
          const dataType = res.type;
          const blob = new Blob([res], { type: dataType });
          const data = window.URL.createObjectURL(blob);
          const downloadLink = document.createElement('a');
          downloadLink.href = data;
          downloadLink.setAttribute('download', product.name);
          downloadLink.click();
        },
        () => {
          this.toastService.warning('Unable to download the pdf');
        }
      );
  }


  removeInlineStyleProductDescription(productDescription: string) {
    //this will remove all inline styling from the product description
    productDescription = productDescription.replace(new RegExp('style="(.*?)"', 'g'), "");
    productDescription = productDescription.replace(new RegExp('face="(.*?)"', 'g'), "");
    return productDescription;
  }

  ngOnDestroy(): void {
    this.accountSubscription?.unsubscribe();
    this.downloadPdfSubscription?.unsubscribe();
  }

}
